import React, { useState } from 'react';
import { ui } from '../../config/uiSizes';
import { LogLevelBadge } from './LogLevelBadge';
import { LogDetailModal } from '../Modals/Log/LogDetailModal';
import type { MigrationLogEntry } from '../../types/logs';
import Spinner from '../migratorDashboard/Spinner';

interface LogTableProps {
  /** Array of log entries to display */
  logs: MigrationLogEntry[];
  /** Whether data is currently loading */
  loading: boolean;
  /** Optional className for customization */
  className?: string;
}

/**
 * Table component for displaying migration logs
 * Includes sortable columns, row actions, and detail modal
 */
export const LogTable: React.FC<LogTableProps> = ({ logs, loading, className = '' }) => {
  const [selectedLog, setSelectedLog] = useState<MigrationLogEntry | null>(null);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateMessage = (message: string, maxLength = 100) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  const handleViewClick = (log: MigrationLogEntry) => {
    setSelectedLog(log);
  };

  const handleCloseModal = () => {
    setSelectedLog(null);
  };

  if (loading) {
    return (
      <Spinner />
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">No logs found matching your filters.</p>
      </div>
    );
  }

  return (
    <>
      <div className={`overflow-x-auto ${className}`}>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th
                scope="col"
                className={`${ui.page.tableCell} text-left ${ui.text.tableHeader} font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider`}
              >
                Timestamp
              </th>
              <th
                scope="col"
                className={`${ui.page.tableCell} text-left ${ui.text.tableHeader} font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider`}
              >
                Level
              </th>
              <th
                scope="col"
                className={`${ui.page.tableCell} text-left ${ui.text.tableHeader} font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider`}
              >
                Stage
              </th>
              <th
                scope="col"
                className={`${ui.page.tableCell} text-left ${ui.text.tableHeader} font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider`}
              >
                Message
              </th>
              <th
                scope="col"
                className={`${ui.page.tableCell} text-right ${ui.text.tableHeader} font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider`}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {logs.map((log) => (
              <tr
                key={log.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <td className={`${ui.page.tableCell} whitespace-nowrap ${ui.text.tableCell} text-gray-600 dark:text-gray-400`}>
                  {formatTimestamp(log.timestamp)}
                </td>
                <td className={`${ui.page.tableCell} whitespace-nowrap`}>
                  <LogLevelBadge level={log.level} />
                </td>
                <td className={`${ui.page.tableCell} whitespace-nowrap ${ui.text.tableCell} text-gray-700 dark:text-gray-300`}>
                  {log.stage}
                </td>
                <td className={`${ui.page.tableCell} ${ui.text.tableCell} text-gray-700 dark:text-gray-300`}>
                  {truncateMessage(log.message)}
                </td>
                <td className={`${ui.page.tableCell} whitespace-nowrap text-right`}>
                  <button
                    onClick={() => handleViewClick(log)}
                    className={`${ui.text.label} text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium focus:outline-none focus:underline`}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedLog && <LogDetailModal log={selectedLog} isOpen={!!selectedLog} onClose={handleCloseModal} />}
    </>
  );
};
